import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { nanoid } from "nanoid";
import {
  Card,
  Grid,
  Stack,
  Text,
  Title,
  Button,
  Box,
  Divider,
  TextInput,
  Textarea,
  ColorInput,
  Paper,
  Group,
  ActionIcon,
  Badge,
  Tabs,
  Slider,
  SegmentedControl,
  Select,
  Container,
  SimpleGrid,
  Image,
  Tooltip,
  Accordion,
  Switch,
  Modal,
  CopyButton,
  Code,
} from "@mantine/core";
import {
  IconTrash,
  IconArrowUp,
  IconArrowDown,
  IconCopy,
  IconDeviceDesktop,
  IconDeviceTablet,
  IconDeviceMobile,
  IconEye,
  IconCheck,
  IconPlus,
  IconPalette,
  IconTypography,
  IconBarbell,
  IconChecklist,
  IconPhone,
  IconDeviceFloppy,
  IconAdjustmentsHorizontal,
  IconLayersSubtract,
  IconFlame,
  IconAward,
  IconClock,
  IconCar,
  IconApple,
  IconStar,
  IconMapPin,
  IconMail,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandYoutube,
  IconBrandWhatsapp,
  IconActivity,
  IconHeart,
  IconRotate,
  IconSparkles,
  IconDownload,
  IconQrcode,
} from "@tabler/icons-react";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  limit,
  doc,
  addDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import { ShareLandingPageModal } from "./ShareLandingPageModal";
import BenchSettingsBreadcrumbs from "../UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchLoader from "../UIComponent/BenchLoader/BenchLoader";

export type BlockType =
  | "header"
  | "hero"
  | "services"
  | "packages"
  | "why_join"
  | "testimonials"
  | "cta_banner"
  | "faq"
  | "footer";

export interface PageBlock {
  id: string;
  type: BlockType;
  content: Record<string, any>;
  styles: {
    backgroundColor?: string;
    textColor?: string;
    subtextColor?: string;
    accentColor?: string;
    secondaryColor?: string;
    cardBg?: string;
    borderColor?: string;
    fontFamily?: string;
    titleSize?: number;
    bodySize?: number;
    paddingY?: number;
    paddingX?: number;
    borderRadius?: number;
  };
}

const GLOBAL_COLOR_PRESETS = [
  { name: "Coral Flame (Ref)", color: "#f43f5e", secondary: "#ea580c" },
  { name: "Crimson Iron", color: "#dc2626", secondary: "#991b1b" },
  { name: "Volt Gold", color: "#eab308", secondary: "#ca8a04" },
  { name: "Emerald Sport", color: "#10b981", secondary: "#047857" },
  { name: "Cyber Cyan", color: "#06b6d4", secondary: "#0284c7" },
  { name: "Royal Violet", color: "#8b5cf6", secondary: "#6d28d9" },
];

export interface FetchedPackage {
  id: string;
  name: string;
  price: string;
  duration: string;
  features: string;
}

export interface FetchedCompanyData {
  name: string;
  logo: string;
  phone?: string;
  email?: string;
  address?: string;
  services: string[];
  packages?: FetchedPackage[];
  facilities?: string[];
  benefits?: string[];
}

const createDefaultBlock = (
  type: BlockType,
  accent: string = "#f43f5e",
  secondary: string = "#ea580c",
  companyData?: FetchedCompanyData,
): PageBlock => {
  const id = nanoid();

  switch (type) {
    case "header":
      return {
        id,
        type,
        content: {
          brandName: companyData?.name || "FLEXFIT",
          tagline: companyData ? "" : "GYM & FITNESS",
          phone: companyData?.phone || "+91 98765 43210",
          navLinks: "HOME, SERVICES, PACKAGES",
          logoUrl: companyData?.logo || "",
        },
        styles: {
          backgroundColor: accent,
          textColor: "#ffffff",
          subtextColor: "rgba(255,255,255,0.85)",
          accentColor: accent,
          secondaryColor: secondary,
          borderColor: "rgba(255,255,255,0.2)",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 18,
          bodySize: 12,
          paddingY: 16,
          paddingX: 24,
          borderRadius: 8,
        },
      };

    case "hero":
      return {
        id,
        type,
        content: {
          titleLine1: companyData
            ? `JOIN ${companyData.name.toUpperCase()}`
            : "TRANSFORM YOUR BODY,",
          titleLine2: companyData
            ? "TRANSFORM YOUR LIFE"
            : "TRANSFORM YOUR LIFE",
          subtitle: `Join ${companyData?.name || "Bench Fitness"} today and get access to world-class equipment, certified personal trainers, and tailored fitness programs.`,
          bgImageUrl:
            "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?q=80&w=1200&auto=format&fit=crop",
          stat1: "Expert Trainers",
          stat2: "Modern Equipment",
          stat3: "Proven Results",
          formBadge: "LIMITED PASSES",
          formTitle: "ENQUIRE NOW",
          formSubtitle: "Claim your 1-day complimentary fitness pass",
          btnText: "GET MY FREE PASS →",
          privacyNote: "100% Secure. We respect your privacy.",
        },
        styles: {
          backgroundColor: accent,
          textColor: "#ffffff",
          subtextColor: "rgba(255,255,255,0.85)",
          accentColor: accent,
          secondaryColor: secondary,
          cardBg: "#ffffff",
          borderColor: "rgba(255,255,255,0.2)",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 42,
          bodySize: 14,
          paddingY: 64,
          paddingX: 24,
          borderRadius: 16,
        },
      };

    case "services": {
      const svcs = companyData?.services || [];
      return {
        id,
        type,
        content: {
          sectionTitle: "OUR SERVICES",
          sectionSubtitle:
            "Tailored programs to help you smash your fitness goals",
          svc1Title: svcs[0] || "Weight Training",
          svc1Desc: svcs[0]
            ? " "
            : "Build strength with advanced progressive equipment",
          svc2Title: svcs[1] || "Cardio Fitness",
          svc2Desc: svcs[1]
            ? " "
            : "Improve endurance and maximize caloric burn",
          svc3Title: svcs[2] || "CrossFit Zone",
          svc3Desc: svcs[2]
            ? " "
            : "High intensity functional training sessions",
          svc4Title: svcs[3] || "Yoga Classes",
          svc4Desc: svcs[3]
            ? " "
            : "Improve flexibility, recovery and core strength",
          svc5Title: svcs[4] || "Personal Training",
          svc5Desc: svcs[4]
            ? " "
            : "One-on-one tailored guidance with elite coaches",
        },
        styles: {
          backgroundColor: "#ffffff",
          textColor: "#0f172a",
          subtextColor: "#64748b",
          cardBg: "#ffffff",
          borderColor: "#e2e8f0",
          accentColor: accent,
          secondaryColor: secondary,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 14,
          bodySize: 12,
          paddingY: 52,
          paddingX: 24,
          borderRadius: 12,
        },
      };
    }

    case "packages": {
      const pkgs = companyData?.packages || [];
      return {
        id,
        type,
        content: {
          sectionTitle: "MEMBERSHIP PACKAGES",
          sectionSubtitle:
            "Simple, transparent pricing. Choose the right plan tailored to your fitness journey.",
          pkg1Name: pkgs[0]?.name || "BASIC",
          pkg1Price: pkgs[0]?.price || "₹999",
          pkg1Duration: pkgs[0]?.duration || "/month",
          pkg1Feats:
            pkgs[0]?.features || "Gym Access, Basic Equipment, Locker Room",
          pkg2Name: pkgs[1]?.name || "PREMIUM",
          pkg2Price: pkgs[1]?.price || "₹1,499",
          pkg2Duration: pkgs[1]?.duration || "/month",
          pkg2Badge: pkgs[1] ? "BEST VALUE" : "BEST VALUE",
          pkg2Feats:
            pkgs[1]?.features ||
            "All Basic Features, Group Classes, Diet Consultation, Priority Support",
          pkg3Name: pkgs[2]?.name || "ELITE",
          pkg3Price: pkgs[2]?.price || "₹2,499",
          pkg3Duration: pkgs[2]?.duration || "/month",
          pkg3Feats:
            pkgs[2]?.features ||
            "All Premium Features, Personal Training, Custom Diet Plan, Unlimited Access",
        },
        styles: {
          backgroundColor: "#ffffff",
          textColor: "#0f172a",
          subtextColor: "#64748b",
          cardBg: "#ffffff",
          borderColor: "#e2e8f0",
          accentColor: accent,
          secondaryColor: secondary,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 14,
          bodySize: 12,
          paddingY: 52,
          paddingX: 24,
          borderRadius: 16,
        },
      };
    }

    case "why_join": {
      const fb = [
        ...(companyData?.facilities || []),
        ...(companyData?.benefits || []),
      ];
      return {
        id,
        type,
        content: {
          sectionTitle: `WHY JOIN ${companyData?.name ? companyData.name.toUpperCase() : "FLEXFIT"}?`,
          sectionSubtitle:
            "We provide everything you need to reach your goals faster.",
          perk1Title: fb[0] || "",
          perk1Desc: fb[0] ? " " : "",
          perk2Title: fb[1] || "",
          perk2Desc: fb[1] ? " " : "",
          perk3Title: fb[2] || "",
          perk3Desc: fb[2] ? " " : "",
          perk4Title: fb[3] || "",
          perk4Desc: fb[3] ? " " : "",
        },
        styles: {
          backgroundColor: "#ffffff",
          textColor: "#0f172a",
          subtextColor: "#64748b",
          cardBg: "#ffffff",
          borderColor: "#e2e8f0",
          accentColor: accent,
          secondaryColor: secondary,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 14,
          bodySize: 12,
          paddingY: 48,
          paddingX: 24,
          borderRadius: 12,
        },
      };
    }

    case "testimonials":
      return {
        id,
        type,
        content: {
          sectionTitle: "WHAT OUR MEMBERS SAY",
          sectionSubtitle:
            "Real stories from people who transformed their bodies with us.",
          reviewQuote:
            "Bench Fitness has completely transformed my lifestyle. Amazing trainers, positive motivating atmosphere, and top-tier equipment!",
          reviewerName: "Anusha R.",
          reviewerRole: "Member since 2024",
        },
        styles: {
          backgroundColor: "#ffffff",
          textColor: "#0f172a",
          subtextColor: "#64748b",
          cardBg: "#ffffff",
          borderColor: "#e2e8f0",
          accentColor: accent,
          secondaryColor: secondary,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 14,
          bodySize: 13,
          paddingY: 48,
          paddingX: 24,
          borderRadius: 16,
        },
      };

    case "cta_banner":
      return {
        id,
        type,
        content: {
          titleLine1: "READY TO TRANSFORM",
          titleLine2: "YOUR LIFE?",
          subheading: "Fill the form and our team will get in touch with you!",
          btnText: "ENQUIRE NOW →",
          athleteUrl:
            "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?q=80&w=800&auto=format&fit=crop",
        },
        styles: {
          backgroundColor: "#ffffff",
          textColor: "#0f172a",
          subtextColor: "#64748b",
          accentColor: accent,
          secondaryColor: secondary,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 26,
          bodySize: 13,
          paddingY: 36,
          paddingX: 24,
          borderRadius: 12,
        },
      };

    case "faq":
      return {
        id,
        type,
        content: {
          sectionTitle: "FREQUENTLY ASKED QUESTIONS",
          sectionSubtitle: "Everything you need to know about our memberships.",
          q1: "How do I claim my free trial pass?",
          a1: "Fill in your details in the lead pass card above. Our team will verify and activate your pass within 15 minutes.",
          q2: "Are trainers available for general guidance on the floor?",
          a2: "Yes, certified floor trainers are always available to help correct posture, adjust weights, and assist safely.",
          q3: "Can I freeze or pause my membership during travel?",
          a3: "Yes, Premium and Elite memberships come with complimentary freeze options up to 30 days per year.",
          q4: "Are group classes included in the basic package?",
          a4: "Group classes (Yoga, Zumba, CrossFit) are included in Premium and Elite packages, or can be added on as needed.",
        },
        styles: {
          backgroundColor: "#ffffff",
          textColor: "#0f172a",
          subtextColor: "#64748b",
          cardBg: "#ffffff",
          borderColor: "#e2e8f0",
          accentColor: accent,
          secondaryColor: secondary,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 13,
          bodySize: 12,
          paddingY: 48,
          paddingX: 24,
          borderRadius: 12,
        },
      };

    case "footer":
      return {
        id,
        type,
        content: {
          gymName: companyData?.name || "FLEXFIT",
          tagline: companyData ? "" : "GYM & FITNESS",
          desc: "We help you build a stronger, healthier and better you.",
          phone: companyData?.phone || "+91 98765 43210",
          email: companyData?.email || "info@flexfitgym.com",
          address:
            companyData?.address ||
            "123, Fitness Street, Chennai, Tamil Nadu - 600001",
          mapImg:
            "https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=400&auto=format&fit=crop",
          copyright: `© ${new Date().getFullYear()} ${companyData?.name || "FlexFit Gym & Fitness"}. All Rights Reserved.`,
        },
        styles: {
          backgroundColor: "#070b12",
          textColor: "#f8fafc",
          subtextColor: "#94a3b8",
          accentColor: accent,
          secondaryColor: secondary,
          borderColor: "#1e293b",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          titleSize: 16,
          bodySize: 12,
          paddingY: 44,
          paddingX: 24,
          borderRadius: 8,
        },
      };
  }
};

const getStrictReferenceBlocks = (
  accent: string = "#f43f5e",
  secondary: string = "#ea580c",
  companyData?: FetchedCompanyData,
): PageBlock[] => [
  createDefaultBlock("header", accent, secondary, companyData),
  createDefaultBlock("hero", accent, secondary, companyData),
  createDefaultBlock("services", accent, secondary, companyData),
  createDefaultBlock("packages", accent, secondary, companyData),
  createDefaultBlock("why_join", accent, secondary, companyData),
  createDefaultBlock("testimonials", accent, secondary, companyData),
  createDefaultBlock("cta_banner", accent, secondary, companyData),
  createDefaultBlock("faq", accent, secondary, companyData),
  createDefaultBlock("footer", accent, secondary, companyData),
];

const BlockRenderer = ({
  block,
  isSelected,
  onSelect,
  onRemove,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  isFirst,
  isLast,
  viewport,
  companyData,
}: {
  block: PageBlock;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  isFirst: boolean;
  isLast: boolean;
  viewport: "desktop" | "tablet" | "mobile";
  companyData?: FetchedCompanyData;
}) => {
  const s = block.styles;
  const c = block.content;
  const isMobile = viewport === "mobile";
  const isTablet = viewport === "tablet";
  const rad = `${s.borderRadius ?? 12}px`;
  const gradient = `linear-gradient(135deg, ${s.accentColor || "#f43f5e"} 0%, ${s.secondaryColor || "#ea580c"} 100%)`;

  const containerStyle: React.CSSProperties = {
    backgroundColor: s.backgroundColor,
    color: s.textColor,
    fontFamily: s.fontFamily || "'Plus Jakarta Sans', sans-serif",
    paddingTop: `${s.paddingY ?? 40}px`,
    paddingBottom: `${s.paddingY ?? 40}px`,
    paddingLeft: isMobile ? "16px" : `${s.paddingX ?? 24}px`,
    paddingRight: isMobile ? "16px" : `${s.paddingX ?? 24}px`,
    width: "100%",
    boxSizing: "border-box",
  };

  const renderContent = () => {
    switch (block.type) {
      case "header":
        return (
          <Box
            style={containerStyle}
            sx={{
              background: `linear-gradient(135deg, ${s.accentColor || "#f43f5e"} 0%, color-mix(in srgb, ${s.accentColor || "#f43f5e"} 80%, white) 45%, color-mix(in srgb, ${s.accentColor || "#f43f5e"} 35%, white) 100%)`,
              borderBottom: `1px solid ${s.borderColor || "rgba(255,255,255,0.2)"}`,
            }}
          >
            <Box
              sx={{
                maxWidth: "1160px",
                margin: "0 auto",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "12px" : "0",
              }}
            >
              <Group spacing={10} noWrap>
                {c.logoUrl ? (
                  <img
                    src={c.logoUrl}
                    alt="Logo"
                    style={{ height: 38, width: "auto", objectFit: "contain" }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: rad,
                      background: gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 4px 12px ${s.accentColor}40`,
                    }}
                  >
                    <IconBarbell size={20} color="#ffffff" />
                  </Box>
                )}
                <div>
                  <Text
                    weight={900}
                    size="sm"
                    sx={{
                      letterSpacing: "1px",
                      lineHeight: 1.1,
                      color: s.textColor || "#ffffff",
                    }}
                  >
                    {c.brandName || "FLEXFIT"}
                  </Text>
                  <Text
                    size="9px"
                    weight={700}
                    color={s.subtextColor || "rgba(255,255,255,0.85)"}
                    sx={{ letterSpacing: "1.5px" }}
                  >
                    {c.tagline || "GYM & FITNESS"}
                  </Text>
                </div>
              </Group>

              {!isMobile && (
                <Group spacing="lg">
                  {(c.navLinks || "HOME, SERVICES, PACKAGES, ABOUT US, CONTACT")
                    .split(",")
                    .map((item: string) => (
                      <Text
                        key={item}
                        size="11px"
                        weight={700}
                        sx={{
                          color: s.subtextColor || "rgba(255,255,255,0.85)",
                          cursor: "pointer",
                          letterSpacing: "0.5px",
                          "&:hover": { color: s.textColor || "#ffffff" },
                        }}
                        onClick={() => {
                          const name = item.trim().toLowerCase();
                          let target = name;
                          if (name === "home") target = "hero";
                          else if (name === "about us") target = "why_join";
                          else if (name === "contact") target = "footer";

                          const el = document.getElementById(
                            `section-${target}`,
                          );
                          if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        {item.trim()}
                      </Text>
                    ))}
                </Group>
              )}

              {c.phone && (
                <Button
                  size={isMobile ? "xs" : "sm"}
                  radius="xl"
                  leftIcon={<IconPhone size={13} />}
                  sx={{
                    background: gradient,
                    fontWeight: 700,
                    fontSize: isMobile ? "11px" : "12px",
                  }}
                >
                  {c.phone}
                </Button>
              )}
            </Box>
          </Box>
        );

      case "hero": {
        // High-contrast guards against dark text on dark hero canvas
        const heroTitleColor =
          s.textColor &&
          !["#0f172a", "#000000", "#1e293b", "#020617"].includes(
            s.textColor.toLowerCase(),
          )
            ? s.textColor
            : "#ffffff";

        const heroSubtitleColor =
          s.subtextColor &&
          !["#64748b", "#0f172a", "#334155", "#1e293b"].includes(
            s.subtextColor.toLowerCase(),
          )
            ? s.subtextColor
            : "#cbd5e1";

        const formBadge = c.formBadge || "LIMITED PASSES";
        const formTitle = c.formTitle || "ENQUIRE NOW";
        const formSubtitle = c.formSubtitle || "";
        const btnText = c.btnText || "GET MY FREE PASS →";
        const privacyNote =
          c.privacyNote || "100% Secure. We respect your privacy.";
        const stat1 = c.stat1 || "Expert Trainers";
        const stat2 = c.stat2 || "Modern Equipment";
        const stat3 = c.stat3 || "Proven Results";

        return (
          <Box
            style={containerStyle}
            sx={{
              position: "relative",
              overflow: "hidden",
              // Gym background photo
              backgroundImage: `url(${
                c.bgImageUrl ||
                "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1400&auto=format&fit=crop"
              })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              minHeight: isMobile ? "auto" : "520px",
              padding: 0,
            }}
          >
            {/* Dynamic accent color overlay — adapts to any chosen color */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(125deg,
                  ${s.accentColor || "#f43f5e"}e6 0%,
                  ${s.accentColor || "#f43f5e"}99 35%,
                  ${s.secondaryColor || "#ea580c"}55 60%,
                  rgba(0,0,0,0.75) 100%)`,
                zIndex: 1,
              }}
            />

            {/* Content sits above overlay */}
            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                maxWidth: "1160px",
                margin: "0 auto",
                padding: isMobile
                  ? "40px 16px"
                  : `${s.paddingY ?? 60}px ${s.paddingX ?? 24}px`,
                display: "grid",
                gridTemplateColumns:
                  isMobile || isTablet ? "1fr" : "1.25fr 0.85fr",
                gap: isMobile ? "32px" : "48px",
                alignItems: "center",
              }}
            >
              <Box>
                {/* Hero Main Headline */}
                <Title
                  order={1}
                  sx={{
                    fontSize: isMobile ? "2rem" : `${s.titleSize || 42}px`,
                    fontWeight: 900,
                    lineHeight: 1.08,
                    letterSpacing: "-0.03em",
                    color: "#ffffff !important",
                    textShadow: "0 2px 12px rgba(0,0,0,0.35)",
                  }}
                >
                  {c.titleLine1 || "TRANSFORM YOUR BODY,"}
                  <br />
                  <Text
                    component="span"
                    inherit
                    sx={{
                      color: "#ffffff !important",
                      fontStyle: "italic",
                    }}
                  >
                    {c.titleLine2 || "TRANSFORM YOUR LIFE"}
                  </Text>
                </Title>

                {/* Hero Subtitle */}
                <Text
                  size="sm"
                  sx={{
                    color: "rgba(255,255,255,0.9) !important",
                    marginTop: 16,
                    maxWidth: 480,
                    lineHeight: 1.65,
                    fontSize: `${s.bodySize || 14}px`,
                    textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                  }}
                >
                  {c.subtitle ||
                    "Join Bench Fitness today and get access to world-class equipment, certified personal trainers, and tailored fitness programs."}
                </Text>

                {/* 3 Quick Stat Callouts */}
                <Group spacing={isMobile ? "md" : "xl"} mt={32}>
                  {[
                    { icon: IconAward, text: stat1 },
                    { icon: IconBarbell, text: stat2 },
                    { icon: IconFlame, text: stat3 },
                  ].map((stat, idx) => (
                    <Stack key={idx} spacing={6} align="center">
                      <Box
                        p={10}
                        sx={{
                          borderRadius: "12px",
                          border: "1px solid rgba(255,255,255,0.3)",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          backdropFilter: "blur(8px)",
                          boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                        }}
                      >
                        <stat.icon size={22} color="#ffffff" />
                      </Box>
                      <Text
                        size="11px"
                        weight={700}
                        sx={{
                          color: "#ffffff",
                          textAlign: "center",
                          textShadow: "0 1px 4px rgba(0,0,0,0.4)",
                        }}
                      >
                        {stat.text}
                      </Text>
                    </Stack>
                  ))}
                </Group>
              </Box>

              {/* Lead Registration Form Card */}
              <Box>
                <Card
                  p={0}
                  sx={{
                    borderRadius: rad,
                    overflow: "hidden",
                    backgroundColor: s.cardBg || "#ffffff",
                    boxShadow: "0 25px 60px -12px rgba(0,0,0,0.7)",
                    border: "none",
                  }}
                >
                  {/* Card Header with Vibrant Gradient & Pure White Labels */}
                  <Box
                    p="md"
                    sx={{
                      background: gradient,
                      textAlign: "center",
                      color: "#ffffff",
                    }}
                  >
                    <Text
                      size="10px"
                      weight={800}
                      sx={{
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        color: "rgba(255, 255, 255, 0.95)",
                      }}
                    >
                      {formBadge}
                    </Text>
                    <Title
                      order={3}
                      weight={900}
                      mt={3}
                      sx={{
                        color: "#ffffff !important",
                        letterSpacing: "0.5px",
                        fontSize: "20px",
                      }}
                    >
                      {formTitle}
                    </Title>
                    <Text
                      size="xs"
                      sx={{ color: "rgba(255, 255, 255, 0.9)", marginTop: 2 }}
                    >
                      {formSubtitle}
                    </Text>
                  </Box>

                  {/* Form Inputs & Action Button */}
                  <Box p="lg">
                    <Stack spacing="xs">
                      <TextInput
                        placeholder="Full Name"
                        size="xs"
                        radius="sm"
                        disabled
                        styles={{
                          input: {
                            backgroundColor: "#f8fafc",
                            borderColor: "#e2e8f0",
                          },
                        }}
                      />
                      <TextInput
                        placeholder="Phone Number"
                        size="xs"
                        radius="sm"
                        disabled
                        styles={{
                          input: {
                            backgroundColor: "#f8fafc",
                            borderColor: "#e2e8f0",
                          },
                        }}
                      />
                      <TextInput
                        placeholder="Email Address"
                        size="xs"
                        radius="sm"
                        disabled
                        styles={{
                          input: {
                            backgroundColor: "#f8fafc",
                            borderColor: "#e2e8f0",
                          },
                        }}
                      />
                      <TextInput
                        placeholder="Select Fitness Goal"
                        size="xs"
                        radius="sm"
                        disabled
                        styles={{
                          input: {
                            backgroundColor: "#f8fafc",
                            borderColor: "#e2e8f0",
                          },
                        }}
                      />
                      <Button
                        fullWidth
                        size="sm"
                        radius="sm"
                        sx={{
                          background: `${gradient} !important`,
                          color: "#ffffff !important",
                          fontWeight: 800,
                          letterSpacing: "0.8px",
                          boxShadow: `0 6px 20px ${s.accentColor || "#f43f5e"}50`,
                          border: "none",
                          minHeight: "40px",
                        }}
                      >
                        <Text color="#ffffff" weight={800} size="xs">
                          {btnText}
                        </Text>
                      </Button>
                      <Text
                        size="10px"
                        align="center"
                        mt={4}
                        sx={{ color: "#64748b" }}
                      >
                        🔒 {privacyNote}
                      </Text>
                    </Stack>
                  </Box>
                </Card>
              </Box>
            </Box>
          </Box>
        );
      }

      case "services":
        return (
          <Box style={containerStyle}>
            <Box
              sx={{ maxWidth: "1160px", margin: "0 auto", textAlign: "center" }}
            >
              <Text
                weight={900}
                size="xs"
                color={s.accentColor}
                sx={{ letterSpacing: "2.5px" }}
              >
                — {c.sectionTitle || "OUR SERVICES"} —
              </Text>
              {c.sectionSubtitle && (
                <Text size="xs" color={s.subtextColor} mt={4}>
                  {c.sectionSubtitle}
                </Text>
              )}

              {(() => {
                const rawServices = Array.from(
                  new Set(companyData?.services || []),
                ).filter(Boolean);
                const hiddenServices: string[] = c.hiddenServices || [];
                const allServices = rawServices.filter(
                  (s) => !hiddenServices.includes(s),
                );

                const displayServices =
                  allServices.length > 0
                    ? allServices
                    : [
                        c.svc1Title,
                        c.svc2Title,
                        c.svc3Title,
                        c.svc4Title,
                        c.svc5Title,
                      ].filter(Boolean);

                const marqueeContent = [
                  ...displayServices,
                  ...displayServices,
                  ...displayServices,
                ];
                const iconsList = [
                  IconBarbell,
                  IconHeart,
                  IconActivity,
                  IconFlame,
                  IconAward,
                  IconCheck,
                ];
                const descList = [
                  "Build strength with advanced progressive equipment",
                  "Improve endurance and maximize caloric burn",
                  "High intensity functional training sessions",
                  "Improve flexibility, recovery and core strength",
                  "One-on-one tailored guidance with elite coaches",
                  "Premium facilities and guided support for your goals",
                ];

                return (
                  <Box
                    sx={{
                      overflow: "hidden",
                      position: "relative",
                      width: "100%",
                      padding: "20px 0",
                    }}
                  >
                    {!isMobile && (
                      <style>{`
                        @keyframes services-marquee {
                          0% { transform: translateX(0); }
                          100% { transform: translateX(-33.3333%); }
                        }
                      `}</style>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        width: isMobile ? "100%" : "max-content",
                        animation: isMobile
                          ? "none"
                          : "services-marquee 40s linear infinite",
                        overflowX: isMobile ? "auto" : "visible",
                        scrollSnapType: isMobile ? "x mandatory" : "none",
                        paddingBottom: isMobile ? "10px" : "0",
                        "&:hover": {
                          animationPlayState: isMobile ? "running" : "paused",
                        },
                        "&::-webkit-scrollbar": { display: "none" },
                        msOverflowStyle: "none",
                        scrollbarWidth: "none",
                      }}
                    >
                      {marqueeContent.map((svc, idx) => {
                        const IconCmp = iconsList[idx % iconsList.length];
                        const desc = descList[idx % descList.length];
                        return (
                          <Paper
                            key={idx}
                            p="xl"
                            withBorder
                            sx={{
                              borderRadius: rad,
                              backgroundColor: s.cardBg,
                              borderColor: s.borderColor,
                              minWidth: "120px",
                              maxWidth: "180px",
                              minHeight: "180px",
                              margin: "0 10px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              gap: "12px",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                              flexShrink: 0,
                              scrollSnapAlign: "start",
                              transition:
                                "transform 0.2s ease, box-shadow 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            {c.showIcons !== false && (
                              <Box
                                p={16}
                                sx={{
                                  borderRadius: "50%",
                                  backgroundColor: `${s.accentColor}18`,
                                  color: s.accentColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <IconCmp size={32} />
                              </Box>
                            )}
                            <Box>
                              <Text
                                weight={800}
                                size="md"
                                color={s.textColor}
                                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.3 }}
                              >
                                {svc}
                              </Text>
                              <Text size="10px" color={s.subtextColor} mt={8}>
                                {desc}
                              </Text>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        );

      case "packages":
        return (
          <Box style={containerStyle}>
            <Box sx={{ maxWidth: "1160px", margin: "0 auto" }}>
              <Box sx={{ textAlign: "center", marginBottom: "32px" }}>
                <Text
                  weight={900}
                  size="xs"
                  color={s.accentColor}
                  sx={{ letterSpacing: "2.5px" }}
                >
                  — {c.sectionTitle || "MEMBERSHIP PACKAGES"} —
                </Text>
                {c.sectionSubtitle && (
                  <Text size="xs" color={s.subtextColor} mt={4}>
                    {c.sectionSubtitle}
                  </Text>
                )}
              </Box>

              <SimpleGrid cols={isMobile ? 1 : isTablet ? 2 : 3} spacing="lg">
                {[
                  {
                    name: c.pkg1Name || "BASIC",
                    price: c.pkg1Price || "₹999",
                    duration: c.pkg1Duration || "/month",
                    feats:
                      c.pkg1Feats || "Gym Access, Basic Equipment, Locker Room",
                    featured: false,
                    badge: null,
                    showButton: false,
                  },
                  {
                    name: c.pkg2Name || "PREMIUM",
                    price: c.pkg2Price || "₹1,499",
                    duration: c.pkg2Duration || "/month",
                    feats:
                      c.pkg2Feats ||
                      "All Basic Features, Group Classes, Diet Consultation, Priority Support",
                    featured: true,
                    badge: c.pkg2Badge || "BEST VALUE",
                    showButton: false,
                  },
                  {
                    name: c.pkg3Name || "ELITE",
                    price: c.pkg3Price || "₹2,499",
                    duration: c.pkg3Duration || "/month",
                    feats:
                      c.pkg3Feats ||
                      "All Premium Features, Personal Training, Custom Diet Plan, Unlimited Access",
                    featured: false,
                    badge: null,
                    showButton: true,
                  },
                ].map((pkg, idx) => (
                  <Paper
                    key={idx}
                    p="xl"
                    withBorder
                    sx={{
                      borderRadius: rad,
                      backgroundColor: s.cardBg,
                      borderColor: pkg.featured ? s.accentColor : s.borderColor,
                      ...(pkg.featured && { borderWidth: 2 }),
                      textAlign: "center",
                      position: pkg.featured ? "relative" : undefined,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      ...(pkg.featured && {
                        boxShadow: `0 18px 32px -8px ${s.accentColor}35`,
                        transform:
                          !isMobile && !isTablet ? "scale(1.04)" : "none",
                      }),
                    }}
                  >
                    {pkg.badge && (
                      <Badge
                        size="sm"
                        sx={{
                          position: "absolute",
                          top: -12,
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: gradient,
                          color: "#fff",
                          fontWeight: 800,
                          letterSpacing: "0.5px",
                        }}
                      >
                        {pkg.badge}
                      </Badge>
                    )}
                    <div>
                      <Text
                        weight={800}
                        size="11px"
                        color={s.subtextColor}
                        transform="uppercase"
                        mt={pkg.featured ? 4 : undefined}
                      >
                        {pkg.name}
                      </Text>
                      <Title
                        order={2}
                        weight={900}
                        color={pkg.featured ? s.accentColor : s.textColor}
                        mt={6}
                      >
                        {pkg.price}
                        <Text
                          component="span"
                          size="11px"
                          color={s.subtextColor}
                          weight={500}
                        >
                          {pkg.duration}
                        </Text>
                      </Title>
                      <Divider my="md" sx={{ borderColor: s.borderColor }} />
                      <Stack spacing={10} align="flex-start" mb="xl">
                        {pkg.feats.split(",").map((f: string) => (
                          <Group key={f} spacing={8}>
                            <IconCheck size={15} color={s.accentColor} />
                            <Text
                              size="xs"
                              color={s.textColor}
                              weight={pkg.featured ? 600 : undefined}
                            >
                              {f.trim()}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    </div>
                  </Paper>
                ))}
              </SimpleGrid>
            </Box>
          </Box>
        );

      case "why_join":
        return (
          <Box style={containerStyle}>
            <Box sx={{ maxWidth: "1160px", margin: "0 auto" }}>
              <Box sx={{ textAlign: "center", marginBottom: "32px" }}>
                <Text
                  weight={900}
                  size="xs"
                  color={s.accentColor}
                  sx={{ letterSpacing: "2.5px" }}
                >
                  — {c.sectionTitle || "WHY JOIN FLEXFIT?"} —
                </Text>
                {c.sectionSubtitle && (
                  <Text size="xs" color={s.subtextColor} mt={4}>
                    {c.sectionSubtitle}
                  </Text>
                )}
              </Box>

              {(() => {
                const rawPerks = Array.from(
                  new Set([
                    ...(companyData?.facilities || []),
                    ...(companyData?.benefits || []),
                  ]),
                ).filter(Boolean);

                const hiddenPerks: string[] = c.hiddenPerks || [];
                const allPerks = rawPerks.filter(
                  (p) => !hiddenPerks.includes(p),
                );

                const displayPerks =
                  allPerks.length > 0
                    ? allPerks
                    : [
                        c.perk1Title,
                        c.perk2Title,
                        c.perk3Title,
                        c.perk4Title,
                      ].filter(Boolean);

                const marqueeContent = [
                  ...displayPerks,
                  ...displayPerks,
                  ...displayPerks,
                ];
                const iconsList = [
                  IconClock,
                  IconApple,
                  IconBarbell,
                  IconFlame,
                  IconAward,
                  IconCheck,
                  IconActivity,
                ];

                return (
                  <Box
                    sx={{
                      overflow: "hidden",
                      position: "relative",
                      width: "100%",
                      padding: "10px 0",
                    }}
                  >
                    <style>{`
                      @keyframes marquee-slide {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-33.3333%); }
                      }
                    `}</style>
                    <Box
                      sx={{
                        display: "flex",
                        width: "max-content",
                        animation: "marquee-slide 40s linear infinite",
                        "&:hover": {
                          animationPlayState: "paused",
                        },
                      }}
                    >
                      {marqueeContent.map((perk, idx) => {
                        const IconCmp = iconsList[idx % iconsList.length];
                        return (
                          <Paper
                            key={idx}
                            p="md"
                            withBorder
                            sx={{
                              borderRadius: rad,
                              backgroundColor: s.cardBg,
                              borderColor: s.borderColor,
                              minWidth: "200px",
                              maxWidth: "280px",
                              margin: "0 10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                c.showIcons !== false ? "flex-start" : "center",
                              textAlign:
                                c.showIcons !== false ? "left" : "center",
                              gap: "12px",
                            }}
                          >
                            {c.showIcons !== false && (
                              <Box
                                p={8}
                                sx={{
                                  borderRadius: "50%",
                                  backgroundColor: `${s.accentColor}18`,
                                  color: s.accentColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <IconCmp size={20} />
                              </Box>
                            )}
                            <Text
                              weight={700}
                              size="sm"
                              color={s.textColor}
                              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.3 }}
                            >
                              {perk}
                            </Text>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        );

      case "testimonials":
        return (
          <Box style={containerStyle}>
            <Box
              sx={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}
            >
              <Text
                weight={900}
                size="xs"
                color={s.accentColor}
                sx={{ letterSpacing: "2.5px" }}
              >
                — {c.sectionTitle || "WHAT OUR MEMBERS SAY"} —
              </Text>
              {c.sectionSubtitle && (
                <Text size="xs" color={s.subtextColor} mt={4} mb="md">
                  {c.sectionSubtitle}
                </Text>
              )}

              <Paper
                p="xl"
                withBorder
                sx={{
                  borderRadius: rad,
                  backgroundColor: s.cardBg,
                  borderColor: s.borderColor,
                  boxShadow: "0 14px 30px -8px rgba(0,0,0,0.06)",
                }}
              >
                <Text
                  size="36px"
                  weight={900}
                  color={s.accentColor}
                  sx={{ lineHeight: 0.8 }}
                >
                  “
                </Text>
                <Group position="center" spacing={4} my={8}>
                  {[...Array(5)].map((_, i) => (
                    <IconStar
                      key={i}
                      size={17}
                      fill="#eab308"
                      color="#eab308"
                    />
                  ))}
                </Group>
                <Text
                  size="sm"
                  sx={{
                    fontStyle: "italic",
                    color: s.textColor,
                    lineHeight: 1.7,
                    fontSize: `${s.bodySize || 14}px`,
                  }}
                  mx="auto"
                >
                  {c.reviewQuote ||
                    "Bench Fitness has completely transformed my lifestyle. Amazing trainers, positive motivating atmosphere, and top-tier equipment!"}
                </Text>
                <Divider my="md" sx={{ borderColor: s.borderColor }} />
                <Text weight={800} size="xs" color={s.textColor}>
                  — {c.reviewerName || "Anusha R."}
                </Text>
                <Text size="10px" color={s.subtextColor} mt={2}>
                  {c.reviewerRole || "Member since 2024"}
                </Text>
              </Paper>
            </Box>
          </Box>
        );

      case "cta_banner":
        return (
          <Box
            style={containerStyle}
            sx={{
              backgroundColor: s.backgroundColor || "#f8fafc",
              color: s.textColor || "#0f172a",
            }}
          >
            <Box
              sx={{
                maxWidth: "1160px",
                margin: "0 auto",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "24px",
              }}
            >
              <div>
                <Title
                  order={2}
                  weight={900}
                  sx={{
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                    fontSize: `${s.titleSize || 26}px`,
                    color: s.textColor || "#0f172a",
                  }}
                >
                  {c.titleLine1 || "READY TO TRANSFORM"}{" "}
                  <Text
                    component="span"
                    inherit
                    sx={{
                      background: gradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {c.titleLine2 || "YOUR LIFE?"}
                  </Text>
                </Title>
                <Text
                  size="xs"
                  opacity={0.9}
                  mt={6}
                  color={s.subtextColor || "#64748b"}
                >
                  {c.subheading ||
                    "Fill the form and our team will get in touch with you!"}
                </Text>
              </div>

              <Button
                size="md"
                sx={{
                  background: gradient,
                  color: "#ffffff !important",
                  borderRadius: rad,
                  fontWeight: 800,
                  boxShadow: `0 4px 16px ${s.accentColor}40`,
                }}
              >
                {c.btnText || "ENQUIRE NOW →"}
              </Button>
            </Box>
          </Box>
        );

      case "faq":
        return (
          <Box style={containerStyle}>
            <Box sx={{ maxWidth: "860px", margin: "0 auto" }}>
              <Box sx={{ textAlign: "center", marginBottom: "28px" }}>
                <Text
                  weight={900}
                  size="xs"
                  color={s.accentColor}
                  sx={{ letterSpacing: "2.5px" }}
                >
                  — {c.sectionTitle || "FREQUENTLY ASKED QUESTIONS"} —
                </Text>
                <Text size="xs" color={s.subtextColor} mt={3}>
                  {c.sectionSubtitle ||
                    "Everything you need to know about our memberships."}
                </Text>
              </Box>

              <Accordion variant="separated" radius="md">
                {[
                  {
                    q: c.q1 || "How do I claim my free trial pass?",
                    a:
                      c.a1 ||
                      "Fill in your details in the lead pass card above. Our team will verify and activate your pass within 15 minutes.",
                    id: "q1",
                  },
                  {
                    q:
                      c.q2 ||
                      "Are trainers available for general guidance on the floor?",
                    a:
                      c.a2 ||
                      "Yes, certified floor trainers are always available to help correct posture, adjust weights, and assist safely.",
                    id: "q2",
                  },
                  {
                    q:
                      c.q3 ||
                      "Can I freeze or pause my membership during travel?",
                    a:
                      c.a3 ||
                      "Yes, Premium and Elite memberships come with complimentary freeze options up to 30 days per year.",
                    id: "q3",
                  },
                  {
                    q:
                      c.q4 ||
                      "Are group classes included in the basic package?",
                    a:
                      c.a4 ||
                      "Group classes (Yoga, Zumba, CrossFit) are included in Premium and Elite packages, or can be added on as needed.",
                    id: "q4",
                  },
                ].map((item) => (
                  <Accordion.Item
                    key={item.id}
                    value={item.id}
                    sx={{
                      backgroundColor: s.cardBg,
                      borderColor: s.borderColor,
                      borderRadius: rad,
                    }}
                  >
                    <Accordion.Control>
                      <Text weight={700} size="xs" color={s.textColor}>
                        {item.q}
                      </Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text size="xs" color={s.subtextColor}>
                        {item.a}
                      </Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Box>
          </Box>
        );

      case "footer":
        return (
          <Box style={containerStyle}>
            <Box
              sx={{
                maxWidth: "1160px",
                margin: "0 auto",
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                    ? "1fr 1fr"
                    : "1.4fr 1fr 1.2fr 1.1fr",
                gap: "32px",
              }}
            >
              <div>
                <Group spacing={8}>
                  <Box
                    p={6}
                    sx={{
                      borderRadius: rad,
                      background: gradient,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <IconBarbell size={16} color="#fff" />
                  </Box>
                  <Text
                    weight={900}
                    size="sm"
                    sx={{ letterSpacing: "1px", color: s.textColor }}
                  >
                    {c.gymName || "FLEXFIT"}
                  </Text>
                </Group>
                <Text size="xs" color={s.subtextColor} mt="xs">
                  {c.desc ||
                    "We help you build a stronger, healthier and better you."}
                </Text>
                <Group spacing="xs" mt="md">
                  {[
                    IconBrandFacebook,
                    IconBrandInstagram,
                    IconBrandYoutube,
                    IconBrandWhatsapp,
                  ].map((Icon, i) => (
                    <ActionIcon
                      key={i}
                      variant="light"
                      color="gray"
                      radius="xl"
                      size="sm"
                      sx={{
                        backgroundColor: "#1e293b",
                        color: "#cbd5e1",
                        "&:hover": { color: s.accentColor },
                      }}
                    >
                      <Icon size={14} />
                    </ActionIcon>
                  ))}
                </Group>
              </div>

              <div>
                <Text
                  size="xs"
                  weight={800}
                  color={s.accentColor}
                  transform="uppercase"
                  sx={{ letterSpacing: "1px" }}
                >
                  Quick Links
                </Text>
                <Stack spacing={6} mt="xs">
                  {["Home", "Services", "Packages"].map((item) => (
                    <Text
                      key={item}
                      size="xs"
                      color={s.subtextColor}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { color: s.textColor },
                      }}
                      onClick={() => {
                        const name = item.toLowerCase();
                        let target = name;
                        if (name === "home") target = "hero";

                        const el = document.getElementById(`section-${target}`);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      {item}
                    </Text>
                  ))}
                </Stack>
              </div>

              <div>
                <Text
                  size="xs"
                  weight={800}
                  color={s.accentColor}
                  transform="uppercase"
                  sx={{ letterSpacing: "1px" }}
                >
                  Contact Us
                </Text>
                <Stack spacing={8} mt="xs">
                  <Group spacing="xs">
                    <IconPhone size={14} color={s.accentColor} />
                    <Text size="xs" color={s.subtextColor}>
                      {c.phone || "+91 98765 43210"}
                    </Text>
                  </Group>
                  <Group spacing="xs">
                    <IconMail size={14} color={s.accentColor} />
                    <Text size="xs" color={s.subtextColor}>
                      {c.email || "info@flexfitgym.com"}
                    </Text>
                  </Group>
                  <Group spacing="xs" align="flex-start">
                    <IconMapPin size={16} color={s.accentColor} />
                    <Text size="xs" color={s.subtextColor}>
                      {c.address ||
                        "123, Fitness Street, Chennai, Tamil Nadu - 600001"}
                    </Text>
                  </Group>
                </Stack>
              </div>

              <div>
                <Text
                  size="xs"
                  weight={800}
                  color={s.accentColor}
                  transform="uppercase"
                  sx={{ letterSpacing: "1px" }}
                  mb="xs"
                >
                  Our Location
                </Text>
                <Paper
                  p="xs"
                  withBorder
                  sx={{
                    borderRadius: rad,
                    backgroundColor: "#0d131f",
                    borderColor: s.borderColor,
                    textAlign: "center",
                  }}
                >
                  {c.latitude && c.longitude ? (
                    <iframe
                      src={`https://maps.google.com/maps?q=${c.latitude},${c.longitude}&z=14&output=embed`}
                      style={{
                        height: "70px",
                        width: "100%",
                        border: "none",
                        borderRadius: rad,
                        marginBottom: "10px",
                      }}
                      allowFullScreen
                      title="Map Preview"
                    />
                  ) : (
                    <Image
                      src={
                        c.mapImg ||
                        "https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=400&auto=format&fit=crop"
                      }
                      height={70}
                      radius={rad}
                      alt="Map preview"
                    />
                  )}
                  <Button
                    fullWidth
                    size="xs"
                    mt="xs"
                    component="a"
                    target="_blank"
                    href={
                      c.latitude && c.longitude
                        ? `https://www.google.com/maps?q=${c.latitude},${c.longitude}`
                        : "#"
                    }
                    sx={{
                      background: gradient,
                      borderRadius: rad,
                      fontWeight: 700,
                    }}
                  >
                    VIEW ON MAP
                  </Button>
                </Paper>
              </div>
            </Box>

            <Divider my="lg" sx={{ borderColor: s.borderColor }} />
            <Text size="11px" color={s.subtextColor} align="center">
              {c.copyright ||
                "© 2026 FlexFit Gym & Fitness. All Rights Reserved."}
            </Text>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      id={`section-${block.type.toLowerCase()}`}
      onClick={onSelect}
      sx={{
        position: "relative",
        border: isSelected ? "2px solid #6366f1" : "2px solid transparent",
        cursor: "pointer",
        transition: "border 0.15s ease",
        "&:hover": { borderColor: isSelected ? "#6366f1" : "#94a3b8" },
      }}
    >
      {isSelected && (
        <Group
          spacing={4}
          sx={{
            position: "absolute",
            top: 10,
            right: 12,
            zIndex: 40,
            backgroundColor: "rgba(15, 23, 42, 0.92)",
            backdropFilter: "blur(4px)",
            padding: "4px 8px",
            borderRadius: "8px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
            border: "1px solid #334155",
          }}
        >
          <Tooltip label="Move Up">
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              disabled={isFirst}
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp();
              }}
            >
              <IconArrowUp size={14} color="#f8fafc" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Move Down">
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              disabled={isLast}
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
            >
              <IconArrowDown size={14} color="#f8fafc" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Duplicate">
            <ActionIcon
              size="sm"
              variant="subtle"
              color="indigo"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
            >
              <IconCopy size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Remove Section">
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      )}
      {renderContent()}
    </Box>
  );
};

const SettingsPanel = ({
  block,
  updateBlock,
  companyData,
}: {
  block: PageBlock;
  updateBlock: (block: PageBlock) => void;
  companyData?: FetchedCompanyData;
}) => {
  const updateContent = (key: string, value: any) => {
    updateBlock({ ...block, content: { ...block.content, [key]: value } });
  };

  const updateStyle = (key: string, value: any) => {
    updateBlock({ ...block, styles: { ...block.styles, [key]: value } });
  };

  const handlePackageSelect = (pkgName: string, slotPrefix: string) => {
    const pkg = companyData?.packages?.find((p) => p.name === pkgName);
    if (pkg) {
      updateBlock({
        ...block,
        content: {
          ...block.content,
          [`${slotPrefix}Name`]: pkg.name,
          [`${slotPrefix}Price`]: pkg.price,
          [`${slotPrefix}Duration`]: pkg.duration,
          [`${slotPrefix}Feats`]: pkg.features,
        },
      });
    } else {
      updateContent(`${slotPrefix}Name`, pkgName);
    }
  };

  const s = block.styles;

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      sx={{
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
      }}
    >
      <Group position="apart" mb="sm">
        <Group spacing="xs">
          <Badge color="pink" variant="filled" size="sm">
            {block.type.toUpperCase().replace("_", " ")}
          </Badge>
          <Text size="11px" color="dimmed" weight={600}>
            ID: {block.id.slice(0, 6)}
          </Text>
        </Group>
      </Group>

      <Tabs defaultValue="content" variant="pills" radius="sm">
        <Tabs.List grow mb="md">
          <Tabs.Tab value="content" icon={<IconChecklist size={13} />}>
            Content
          </Tabs.Tab>
          <Tabs.Tab value="styles" icon={<IconPalette size={13} />}>
            Colors & Style
          </Tabs.Tab>
          <Tabs.Tab value="typography" icon={<IconTypography size={13} />}>
            Spacing & Fonts
          </Tabs.Tab>
        </Tabs.List>

        {/* Content Configuration */}
        <Tabs.Panel value="content">
          <Stack spacing="xs">
            {block.type === "header" && (
              <>
                <TextInput
                  label="Gym Brand Name"
                  size="xs"
                  value={block.content.brandName || ""}
                  onChange={(e) => updateContent("brandName", e.target.value)}
                />
                <TextInput
                  label="Tagline"
                  size="xs"
                  value={block.content.tagline || ""}
                  onChange={(e) => updateContent("tagline", e.target.value)}
                />
                <TextInput
                  label="Phone CTA"
                  size="xs"
                  value={block.content.phone || ""}
                  onChange={(e) => updateContent("phone", e.target.value)}
                />
                <TextInput
                  label="Navigation Links (comma-separated)"
                  size="xs"
                  value={block.content.navLinks || ""}
                  onChange={(e) => updateContent("navLinks", e.target.value)}
                />
              </>
            )}

            {block.type === "hero" && (
              <>
                <TextInput
                  label="Headline Line 1"
                  size="xs"
                  value={block.content.titleLine1 || ""}
                  onChange={(e) => updateContent("titleLine1", e.target.value)}
                />
                <TextInput
                  label="Headline Line 2 (Accent Gradient)"
                  size="xs"
                  value={block.content.titleLine2 || ""}
                  onChange={(e) => updateContent("titleLine2", e.target.value)}
                />
                <Textarea
                  label="Subtitle Description"
                  size="xs"
                  rows={3}
                  value={block.content.subtitle || ""}
                  onChange={(e) => updateContent("subtitle", e.target.value)}
                />
                <TextInput
                  label="Hero Background Image URL"
                  size="xs"
                  value={block.content.bgImageUrl || ""}
                  onChange={(e) => updateContent("bgImageUrl", e.target.value)}
                />
                <Divider
                  label="3 Key Features / Stats"
                  labelPosition="center"
                  my={4}
                />
                <TextInput
                  label="Feature 1"
                  size="xs"
                  value={block.content.stat1 || ""}
                  onChange={(e) => updateContent("stat1", e.target.value)}
                />
                <TextInput
                  label="Feature 2"
                  size="xs"
                  value={block.content.stat2 || ""}
                  onChange={(e) => updateContent("stat2", e.target.value)}
                />
                <TextInput
                  label="Feature 3"
                  size="xs"
                  value={block.content.stat3 || ""}
                  onChange={(e) => updateContent("stat3", e.target.value)}
                />
                <Divider
                  label="Lead Registration Pass Card"
                  labelPosition="center"
                  my={4}
                />
                <TextInput
                  label="Card Header Badge"
                  size="xs"
                  value={block.content.formBadge || ""}
                  onChange={(e) => updateContent("formBadge", e.target.value)}
                />
                <TextInput
                  label="Card Header Title"
                  size="xs"
                  value={block.content.formTitle || ""}
                  onChange={(e) => updateContent("formTitle", e.target.value)}
                />
                <TextInput
                  label="Card Header Subtitle"
                  size="xs"
                  value={block.content.formSubtitle || ""}
                  onChange={(e) =>
                    updateContent("formSubtitle", e.target.value)
                  }
                />
                <TextInput
                  label="Submit Button Text"
                  size="xs"
                  value={block.content.btnText || ""}
                  onChange={(e) => updateContent("btnText", e.target.value)}
                />
                <TextInput
                  label="Privacy / Security Note"
                  size="xs"
                  value={block.content.privacyNote || ""}
                  onChange={(e) => updateContent("privacyNote", e.target.value)}
                />
              </>
            )}

            {block.type === "services" && (
              <>
                <TextInput
                  label="Section Title"
                  size="xs"
                  value={block.content.sectionTitle || ""}
                  onChange={(e) =>
                    updateContent("sectionTitle", e.target.value)
                  }
                />
                <TextInput
                  label="Section Subtitle"
                  size="xs"
                  value={block.content.sectionSubtitle || ""}
                  onChange={(e) =>
                    updateContent("sectionSubtitle", e.target.value)
                  }
                />
                <Switch
                  label="Show Icons in Cards"
                  size="sm"
                  checked={block.content.showIcons !== false}
                  onChange={(e) =>
                    updateContent("showIcons", e.currentTarget.checked)
                  }
                  mt="md"
                />

                <Divider
                  label="Visible Services"
                  labelPosition="center"
                  my={16}
                />

                {(() => {
                  const allServices = Array.from(
                    new Set(companyData?.services || []),
                  ).filter(Boolean);

                  if (allServices.length === 0) {
                    return (
                      <Text size="xs" color="dimmed" align="center">
                        No services found.
                      </Text>
                    );
                  }

                  const hiddenServices: string[] =
                    block.content.hiddenServices || [];

                  return (
                    <Stack spacing="xs">
                      {allServices.map((svc, idx) => (
                        <Switch
                          key={idx}
                          label={
                            <Text size="xs" sx={{ lineHeight: 1.2 }}>
                              {svc}
                            </Text>
                          }
                          size="xs"
                          checked={!hiddenServices.includes(svc)}
                          onChange={(e) => {
                            const isChecked = e.currentTarget.checked;
                            let newHidden = [...hiddenServices];
                            if (isChecked) {
                              newHidden = newHidden.filter((s) => s !== svc);
                            } else {
                              if (!newHidden.includes(svc)) newHidden.push(svc);
                            }
                            updateContent("hiddenServices", newHidden);
                          }}
                        />
                      ))}
                    </Stack>
                  );
                })()}
              </>
            )}

            {block.type === "packages" && (
              <>
                <TextInput
                  label="Section Title"
                  size="xs"
                  value={block.content.sectionTitle || ""}
                  onChange={(e) =>
                    updateContent("sectionTitle", e.target.value)
                  }
                />
                <TextInput
                  label="Section Subtitle"
                  size="xs"
                  value={block.content.sectionSubtitle || ""}
                  onChange={(e) =>
                    updateContent("sectionSubtitle", e.target.value)
                  }
                />
                <Divider label="Tier 1 (Basic)" labelPosition="center" my={4} />
                <Select
                  label="Name (Select to Auto-fill)"
                  size="xs"
                  data={
                    companyData?.packages?.map((p) => ({
                      value: p.name,
                      label: p.name,
                    })) || []
                  }
                  value={block.content.pkg1Name || ""}
                  onChange={(val) => handlePackageSelect(val || "", "pkg1")}
                  clearable
                  searchable
                />
                <Group grow>
                  <TextInput
                    label="Price"
                    size="xs"
                    value={block.content.pkg1Price || ""}
                    onChange={(e) => updateContent("pkg1Price", e.target.value)}
                  />
                  <TextInput
                    label="Duration"
                    size="xs"
                    value={block.content.pkg1Duration || ""}
                    onChange={(e) =>
                      updateContent("pkg1Duration", e.target.value)
                    }
                  />
                </Group>
                <TextInput
                  label="Features (comma-separated)"
                  size="xs"
                  value={block.content.pkg1Feats || ""}
                  onChange={(e) => updateContent("pkg1Feats", e.target.value)}
                />

                <Divider
                  label="Tier 2 (Featured)"
                  labelPosition="center"
                  my={4}
                />
                <Select
                  label="Name (Select to Auto-fill)"
                  size="xs"
                  data={
                    companyData?.packages?.map((p) => ({
                      value: p.name,
                      label: p.name,
                    })) || []
                  }
                  value={block.content.pkg2Name || ""}
                  onChange={(val) => handlePackageSelect(val || "", "pkg2")}
                  clearable
                  searchable
                />
                <Group grow>
                  <TextInput
                    label="Badge"
                    size="xs"
                    value={block.content.pkg2Badge || ""}
                    onChange={(e) => updateContent("pkg2Badge", e.target.value)}
                  />
                  <TextInput
                    label="Price"
                    size="xs"
                    value={block.content.pkg2Price || ""}
                    onChange={(e) => updateContent("pkg2Price", e.target.value)}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Duration"
                    size="xs"
                    value={block.content.pkg2Duration || ""}
                    onChange={(e) =>
                      updateContent("pkg2Duration", e.target.value)
                    }
                  />
                </Group>
                <TextInput
                  label="Features (comma-separated)"
                  size="xs"
                  value={block.content.pkg2Feats || ""}
                  onChange={(e) => updateContent("pkg2Feats", e.target.value)}
                />

                <Divider label="Tier 3 (Elite)" labelPosition="center" my={4} />
                <Select
                  label="Name (Select to Auto-fill)"
                  size="xs"
                  data={
                    companyData?.packages?.map((p) => ({
                      value: p.name,
                      label: p.name,
                    })) || []
                  }
                  value={block.content.pkg3Name || ""}
                  onChange={(val) => handlePackageSelect(val || "", "pkg3")}
                  clearable
                  searchable
                />
                <Group grow>
                  <TextInput
                    label="Price"
                    size="xs"
                    value={block.content.pkg3Price || ""}
                    onChange={(e) => updateContent("pkg3Price", e.target.value)}
                  />
                  <TextInput
                    label="Duration"
                    size="xs"
                    value={block.content.pkg3Duration || ""}
                    onChange={(e) =>
                      updateContent("pkg3Duration", e.target.value)
                    }
                  />
                </Group>
                <TextInput
                  label="Features (comma-separated)"
                  size="xs"
                  value={block.content.pkg3Feats || ""}
                  onChange={(e) => updateContent("pkg3Feats", e.target.value)}
                />
              </>
            )}

            {block.type === "why_join" && (
              <>
                <TextInput
                  label="Section Title"
                  size="xs"
                  value={block.content.sectionTitle || ""}
                  onChange={(e) =>
                    updateContent("sectionTitle", e.target.value)
                  }
                />
                <TextInput
                  label="Section Subtitle"
                  size="xs"
                  value={block.content.sectionSubtitle || ""}
                  onChange={(e) =>
                    updateContent("sectionSubtitle", e.target.value)
                  }
                />
                <Switch
                  label="Show Icons in Cards"
                  size="sm"
                  checked={block.content.showIcons !== false}
                  onChange={(e) =>
                    updateContent("showIcons", e.currentTarget.checked)
                  }
                  mt="md"
                />

                <Divider label="Visible Perks" labelPosition="center" my={16} />

                {(() => {
                  const allPerks = Array.from(
                    new Set([
                      ...(companyData?.facilities || []),
                      ...(companyData?.benefits || []),
                    ]),
                  ).filter(Boolean);

                  if (allPerks.length === 0) {
                    return (
                      <Text size="xs" color="dimmed" align="center">
                        No facilities or benefits found.
                      </Text>
                    );
                  }

                  const hiddenPerks: string[] = block.content.hiddenPerks || [];

                  return (
                    <Stack spacing="xs">
                      {allPerks.map((perk, idx) => (
                        <Switch
                          key={idx}
                          label={
                            <Text size="xs" sx={{ lineHeight: 1.2 }}>
                              {perk}
                            </Text>
                          }
                          size="xs"
                          checked={!hiddenPerks.includes(perk)}
                          onChange={(e) => {
                            const isChecked = e.currentTarget.checked;
                            let newHidden = [...hiddenPerks];
                            if (isChecked) {
                              newHidden = newHidden.filter((p) => p !== perk);
                            } else {
                              if (!newHidden.includes(perk))
                                newHidden.push(perk);
                            }
                            updateContent("hiddenPerks", newHidden);
                          }}
                        />
                      ))}
                    </Stack>
                  );
                })()}
              </>
            )}

            {block.type === "testimonials" && (
              <>
                <TextInput
                  label="Section Title"
                  size="xs"
                  value={block.content.sectionTitle || ""}
                  onChange={(e) =>
                    updateContent("sectionTitle", e.target.value)
                  }
                />
                <TextInput
                  label="Section Subtitle"
                  size="xs"
                  value={block.content.sectionSubtitle || ""}
                  onChange={(e) =>
                    updateContent("sectionSubtitle", e.target.value)
                  }
                />
                <Textarea
                  label="Review Quote"
                  size="xs"
                  rows={3}
                  value={block.content.reviewQuote || ""}
                  onChange={(e) => updateContent("reviewQuote", e.target.value)}
                />
                <TextInput
                  label="Member Name"
                  size="xs"
                  value={block.content.reviewerName || ""}
                  onChange={(e) =>
                    updateContent("reviewerName", e.target.value)
                  }
                />
                <TextInput
                  label="Member Role / Tenure"
                  size="xs"
                  value={block.content.reviewerRole || ""}
                  onChange={(e) =>
                    updateContent("reviewerRole", e.target.value)
                  }
                />
              </>
            )}

            {block.type === "cta_banner" && (
              <>
                <TextInput
                  label="Headline 1"
                  size="xs"
                  value={block.content.titleLine1 || ""}
                  onChange={(e) => updateContent("titleLine1", e.target.value)}
                />
                <TextInput
                  label="Headline 2 (Gradient Highlight)"
                  size="xs"
                  value={block.content.titleLine2 || ""}
                  onChange={(e) => updateContent("titleLine2", e.target.value)}
                />
                <TextInput
                  label="Subheading Offer"
                  size="xs"
                  value={block.content.subheading || ""}
                  onChange={(e) => updateContent("subheading", e.target.value)}
                />
                <TextInput
                  label="Button Text"
                  size="xs"
                  value={block.content.btnText || ""}
                  onChange={(e) => updateContent("btnText", e.target.value)}
                />
              </>
            )}

            {block.type === "faq" && (
              <>
                <TextInput
                  label="Section Title"
                  size="xs"
                  value={block.content.sectionTitle || ""}
                  onChange={(e) =>
                    updateContent("sectionTitle", e.target.value)
                  }
                />
                <TextInput
                  label="Question 1"
                  size="xs"
                  value={block.content.q1 || ""}
                  onChange={(e) => updateContent("q1", e.target.value)}
                />
                <Textarea
                  label="Answer 1"
                  size="xs"
                  rows={2}
                  value={block.content.a1 || ""}
                  onChange={(e) => updateContent("a1", e.target.value)}
                />
                <TextInput
                  label="Question 2"
                  size="xs"
                  value={block.content.q2 || ""}
                  onChange={(e) => updateContent("q2", e.target.value)}
                />
                <Textarea
                  label="Answer 2"
                  size="xs"
                  rows={2}
                  value={block.content.a2 || ""}
                  onChange={(e) => updateContent("a2", e.target.value)}
                />
              </>
            )}

            {block.type === "footer" && (
              <>
                <TextInput
                  label="Gym Name"
                  size="xs"
                  value={block.content.gymName || ""}
                  onChange={(e) => updateContent("gymName", e.target.value)}
                />
                <TextInput
                  label="Phone"
                  size="xs"
                  value={block.content.phone || ""}
                  onChange={(e) => updateContent("phone", e.target.value)}
                />
                <TextInput
                  label="Email"
                  size="xs"
                  value={block.content.email || ""}
                  onChange={(e) => updateContent("email", e.target.value)}
                />
                <TextInput
                  label="Address"
                  size="xs"
                  value={block.content.address || ""}
                  onChange={(e) => updateContent("address", e.target.value)}
                />
                <TextInput
                  label="Map Image URL"
                  size="xs"
                  value={block.content.mapImg || ""}
                  onChange={(e) => updateContent("mapImg", e.target.value)}
                />
                <Group grow mt="xs">
                  <TextInput
                    label="Latitude"
                    size="xs"
                    value={block.content.latitude || ""}
                    onChange={(e) => updateContent("latitude", e.target.value)}
                  />
                  <TextInput
                    label="Longitude"
                    size="xs"
                    value={block.content.longitude || ""}
                    onChange={(e) => updateContent("longitude", e.target.value)}
                  />
                </Group>
                <Button
                  size="xs"
                  variant="light"
                  mt="xs"
                  fullWidth
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          updateBlock({
                            ...block,
                            content: {
                              ...block.content,
                              latitude: position.coords.latitude.toString(),
                              longitude: position.coords.longitude.toString(),
                            },
                          });
                        },
                        (error) => {
                          console.error("Error getting location", error);
                          alert("Unable to retrieve your location.");
                        },
                      );
                    } else {
                      alert("Geolocation is not supported by this browser.");
                    }
                  }}
                >
                  Get Current Location
                </Button>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Colors & Visual Styling */}
        <Tabs.Panel value="styles">
          <Stack spacing="xs">
            <Group grow>
              <ColorInput
                label="Section Background"
                size="xs"
                value={s.backgroundColor || ""}
                onChange={(v) => updateStyle("backgroundColor", v)}
              />
              <ColorInput
                label="Card Background"
                size="xs"
                value={s.cardBg || ""}
                onChange={(v) => updateStyle("cardBg", v)}
              />
            </Group>
            <Group grow>
              <ColorInput
                label="Primary Accent (Gradient Start)"
                size="xs"
                value={s.accentColor || ""}
                onChange={(v) => updateStyle("accentColor", v)}
              />
              <ColorInput
                label="Secondary Accent (Gradient End)"
                size="xs"
                value={s.secondaryColor || ""}
                onChange={(v) => updateStyle("secondaryColor", v)}
              />
            </Group>
            <Group grow>
              <ColorInput
                label="Primary Headline Text"
                size="xs"
                value={s.textColor || ""}
                onChange={(v) => updateStyle("textColor", v)}
              />
              <ColorInput
                label="Subtext / Paragraph Color"
                size="xs"
                value={s.subtextColor || ""}
                onChange={(v) => updateStyle("subtextColor", v)}
              />
            </Group>
            <ColorInput
              label="Borders & Dividers"
              size="xs"
              value={s.borderColor || ""}
              onChange={(v) => updateStyle("borderColor", v)}
            />
            <Box mt="xs">
              <Text size="xs" weight={600} mb={4} color="dimmed">
                Corner Radius: {s.borderRadius ?? 12}px
              </Text>
              <Slider
                size="sm"
                min={0}
                max={32}
                step={2}
                value={s.borderRadius ?? 12}
                onChange={(v) => updateStyle("borderRadius", v)}
              />
            </Box>
          </Stack>
        </Tabs.Panel>

        {/* Spacing & Typography */}
        <Tabs.Panel value="typography">
          <Stack spacing="xs">
            <Select
              label="Font Family"
              size="xs"
              value={s.fontFamily}
              onChange={(v) => updateStyle("fontFamily", v)}
              data={[
                {
                  value: "'Plus Jakarta Sans', sans-serif",
                  label: "Plus Jakarta Sans (Default)",
                },
                {
                  value: "'Inter', sans-serif",
                  label: "Inter (Clean & Modern)",
                },
                {
                  value: "'Poppins', sans-serif",
                  label: "Poppins (Geometric)",
                },
                {
                  value: "'Montserrat', sans-serif",
                  label: "Montserrat (Punchy & Bold)",
                },
                {
                  value: "'Oswald', sans-serif",
                  label: "Oswald (Athletic Condensed)",
                },
              ]}
            />
            <Box mt="xs">
              <Text size="xs" weight={600} mb={4} color="dimmed">
                Title Font Size: {s.titleSize || 20}px
              </Text>
              <Slider
                size="sm"
                min={14}
                max={64}
                step={2}
                value={s.titleSize || 20}
                onChange={(v) => updateStyle("titleSize", v)}
              />
            </Box>
            <Box mt="xs">
              <Text size="xs" weight={600} mb={4} color="dimmed">
                Body Font Size: {s.bodySize || 13}px
              </Text>
              <Slider
                size="sm"
                min={10}
                max={22}
                step={1}
                value={s.bodySize || 13}
                onChange={(v) => updateStyle("bodySize", v)}
              />
            </Box>
            <Box mt="xs">
              <Text size="xs" weight={600} mb={4} color="dimmed">
                Vertical Section Padding (Y): {s.paddingY ?? 40}px
              </Text>
              <Slider
                size="sm"
                min={16}
                max={120}
                step={4}
                value={s.paddingY ?? 40}
                onChange={(v) => updateStyle("paddingY", v)}
              />
            </Box>
            <Box mt="xs">
              <Text size="xs" weight={600} mb={4} color="dimmed">
                Horizontal Padding (X): {s.paddingX ?? 24}px
              </Text>
              <Slider
                size="sm"
                min={12}
                max={64}
                step={4}
                value={s.paddingX ?? 24}
                onChange={(v) => updateStyle("paddingX", v)}
              />
            </Box>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
};

const LandingPageBuilder: React.FC = () => {
  const { pageId: pageIdParam } = useParams();
  const navigate = useNavigate();
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );
  const plan = useAppSelector(
    (state: RootState) => state.session?.company_details?.plan || "Starter",
  );

  const [globalAccent, setGlobalAccent] = useState<string>("#f43f5e");
  const [globalSecondary, setGlobalSecondary] = useState<string>("#ea580c");
  const [companyData, setCompanyData] = useState<
    FetchedCompanyData | undefined
  >(undefined);
  const [blocks, setBlocks] = useState<PageBlock[]>(() =>
    getStrictReferenceBlocks("#f43f5e", "#ea580c"),
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  // const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [savedPageUrl, setSavedPageUrl] = useState("");
  const [viewport, setViewport] = useState<"desktop" | "tablet" | "mobile">(
    "desktop",
  );
  const [pageId, setPageId] = useState<string | null>(null);
  const [firestorePageDocId, setFirestorePageDocId] = useState<string | null>(
    null,
  );
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [availableForms, setAvailableForms] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedFormIdUrl, setSelectedFormIdUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) return;
      try {
        // Fetch company profile unconditionally
        let fetchedComp: FetchedCompanyData = {
          name: "",
          logo: "",
          services: [],
          facilities: [],
          benefits: [],
        };
        const compDocRef = doc(db, "companyprofile", companyId);
        const compDoc = await getDoc(compDocRef);

        if (compDoc.exists()) {
          const data = compDoc.data();
          fetchedComp.name = data.name || data.company_name || "";
          fetchedComp.logo = data.company_logo || data.logo || "";
          fetchedComp.phone = data.mobile_number || "";
          fetchedComp.email = data.email || "";
          const addressParts = [
            data.address_1,
            data.address_2,
            data.address_3,
            data.city,
            data.state,
            data.pincode,
          ].filter(Boolean);
          fetchedComp.address = addressParts.join(", ");

          // Extract facilities and benefits from company profile
          if (data.facilities && Array.isArray(data.facilities)) {
            console.log("Raw companyprofile facilities:", data.facilities);
            data.facilities.forEach((f: any) => {
              if (typeof f === "string")
                fetchedComp.facilities = [...(fetchedComp.facilities || []), f];
              else if (typeof f === "object" && f !== null) {
                if (f.title)
                  fetchedComp.facilities = [
                    ...(fetchedComp.facilities || []),
                    f.title,
                  ];
                else if (f.name)
                  fetchedComp.facilities = [
                    ...(fetchedComp.facilities || []),
                    f.name,
                  ];
                else
                  fetchedComp.facilities = [
                    ...(fetchedComp.facilities || []),
                    JSON.stringify(f),
                  ];
              }
            });
          }
          if (data.benefits && Array.isArray(data.benefits)) {
            console.log("Raw companyprofile benefits:", data.benefits);
            data.benefits.forEach((b: any) => {
              if (typeof b === "string")
                fetchedComp.benefits = [...(fetchedComp.benefits || []), b];
              else if (typeof b === "object" && b !== null) {
                if (b.title)
                  fetchedComp.benefits = [
                    ...(fetchedComp.benefits || []),
                    b.title,
                  ];
                else if (b.name)
                  fetchedComp.benefits = [
                    ...(fetchedComp.benefits || []),
                    b.name,
                  ];
                else
                  fetchedComp.benefits = [
                    ...(fetchedComp.benefits || []),
                    JSON.stringify(b),
                  ];
              }
            });
          }
        }

        // Fetch branches to aggregate services
        const branchesSnap = await getDocs(
          query(collection(db, "branch"), where("company_id", "==", companyId)),
        );
        const allServices = new Set<string>();
        const allFacilities = new Set<string>();
        const allBenefits = new Set<string>();

        await Promise.all(
          branchesSnap.docs.map(async (branchDoc) => {
            const bData = branchDoc.data();

            // Check main document arrays first (fallback)
            if (bData.services && Array.isArray(bData.services)) {
              bData.services.forEach((s: any) => {
                if (typeof s === "string") {
                  allServices.add(s);
                } else if (typeof s === "object" && s !== null && s.name) {
                  allServices.add(s.name);
                }
              });
            }
            if (bData.facilities && Array.isArray(bData.facilities)) {
              bData.facilities.forEach((f: any) => {
                if (typeof f === "string") {
                  allFacilities.add(f);
                } else if (typeof f === "object" && f !== null) {
                  if (f.title) allFacilities.add(f.title);
                  else if (f.name) allFacilities.add(f.name);
                }
              });
            }
            if (bData.benefits && Array.isArray(bData.benefits)) {
              bData.benefits.forEach((b: any) => {
                if (typeof b === "string") {
                  allBenefits.add(b);
                } else if (typeof b === "object" && b !== null) {
                  if (b.title) allBenefits.add(b.title);
                  else if (b.name) allBenefits.add(b.name);
                }
              });
            }

            // Query the subcollections for this branch
            try {
              const facSnap = await getDocs(
                query(
                  collection(db, "branch", branchDoc.id, "facilities"),
                  where("company_id", "==", companyId),
                ),
              );
              facSnap.forEach((doc) => {
                const data = doc.data();
                if (data.title) allFacilities.add(data.title);
                else if (data.name) allFacilities.add(data.name);
                else allFacilities.add(JSON.stringify(data));
              });
            } catch (e: any) {
              console.error(`facilities error: ${e?.message || e}`);
            }

            try {
              const benSnap = await getDocs(
                query(
                  collection(db, "branch", branchDoc.id, "benefits"),
                  where("company_id", "==", companyId),
                ),
              );
              benSnap.forEach((doc) => {
                const data = doc.data();
                if (data.title) allBenefits.add(data.title);
                else if (data.name) allBenefits.add(data.name);
                else allBenefits.add(JSON.stringify(data));
              });
            } catch (e: any) {
              console.error(`benefits error: ${e?.message || e}`);
            }

            try {
              const serSnap = await getDocs(
                query(
                  collection(db, "branch", branchDoc.id, "services"),
                  where("company_id", "==", companyId),
                ),
              );
              serSnap.forEach((doc) => {
                const data = doc.data();
                if (data.name) allServices.add(data.name);
                else if (data.title) allServices.add(data.title);
                else allServices.add(JSON.stringify(data));
              });
            } catch (e: any) {
              console.error(`services error: ${e?.message || e}`);
            }
          }),
        );

        fetchedComp.services = Array.from(
          new Set([
            ...(fetchedComp.services || []),
            ...Array.from(allServices),
          ]),
        );
        fetchedComp.facilities = Array.from(
          new Set([
            ...(fetchedComp.facilities || []),
            ...Array.from(allFacilities),
          ]),
        );
        fetchedComp.benefits = Array.from(
          new Set([
            ...(fetchedComp.benefits || []),
            ...Array.from(allBenefits),
          ]),
        );

        try {
          // Fetch packages
          const pkgsSnap = await getDocs(
            query(
              collection(db, "packages"),
              where("company_id", "==", companyId),
            ),
          );
          const pkgs: FetchedPackage[] = [];
          pkgsSnap.forEach((doc) => {
            const data = doc.data();
            pkgs.push({
              id: doc.id,
              name: data.package_name || data.packageName || "Package",
              price: data.package_price ? `₹${data.package_price}` : "Free",
              duration:
                data.package_duration_type === "months"
                  ? `/${data.package_number_of_days} months`
                  : data.package_duration_type === "days"
                    ? `/${data.package_number_of_days} days`
                    : "/month",
              features: "Gym Access, Standard Facilities", // Basic defaults
            });
          });
          fetchedComp.packages = pkgs;
        } catch (pkgErr) {
          console.warn("Failed to fetch packages for defaults:", pkgErr);
        }

        try {
          const formsSnap = await getDocs(
            query(
              collection(db, "customforms"),
              where("company_id", "==", companyId),
            ),
          );
          const formsList: { value: string; label: string }[] = [];
          formsSnap.forEach((doc) => {
            const data = doc.data();
            const actualFormId = data.form_id || doc.id;
            formsList.push({
              value: actualFormId,
              label: data.title,
            });
          });
          setAvailableForms(formsList);
        } catch (formErr) {
          console.error("Failed to fetch custom forms:", formErr);
        }

        setCompanyData(fetchedComp);

        if (pageIdParam) {
          const pageQ = query(
            collection(db, "customlandingpages"),
            where("company_id", "==", companyId),
            where("page_id", "==", pageIdParam),
            limit(1),
          );
          const pageSnapshot = await getDocs(pageQ);

          let pageDoc: any = null;
          let pageData: any = null;

          if (!pageSnapshot.empty) {
            pageDoc = pageSnapshot.docs[0];
            pageData = pageDoc.data();
          } else {
            const docRef = doc(db, "customlandingpages", pageIdParam);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists() && docSnap.data().company_id === companyId) {
              pageDoc = docSnap;
              pageData = docSnap.data();
            }
          }

          if (pageDoc && pageData) {
            setFirestorePageDocId(pageDoc.id);
            setPageId(pageData.page_id || pageDoc.id);
            const baseUrl =
              process.env.REACT_APP_CURRENT_DOMAIN_NAME ||
              window.location.origin;
            setSavedPageUrl(
              `${baseUrl}/LandingPage/pageHtml?page_id=${pageData.page_id || pageDoc.id}`,
            );
            if (pageData.form_id) {
              setSelectedFormIdUrl(pageData.form_id);
            }
            if (pageData.blocks && pageData.blocks.length > 0) {
              // Merge saved blocks but update why_join perks with real fetched data
              const savedBlocks = pageData.blocks;
              const fb = fetchedComp.facilities || [];
              const bb = fetchedComp.benefits || [];
              const merged = savedBlocks.map((block: any) => {
                if (block.type === "why_join") {
                  return {
                    ...block,
                    content: {
                      ...block.content,
                      perk1Title: fb[0] || block.content.perk1Title || "",
                      perk2Title: fb[1] || block.content.perk2Title || "",
                      perk3Title: bb[0] || block.content.perk3Title || "",
                      perk4Title: bb[1] || block.content.perk4Title || "",
                    },
                  };
                }
                if (block.type === "header") {
                  return {
                    ...block,
                    content: {
                      ...block.content,
                      phone: fetchedComp.phone || block.content.phone || "",
                      brandName:
                        fetchedComp.name || block.content.brandName || "",
                      logoUrl: fetchedComp.logo || block.content.logoUrl || "",
                    },
                  };
                }
                if (block.type === "footer") {
                  return {
                    ...block,
                    content: {
                      ...block.content,
                      gymName: fetchedComp.name || block.content.gymName || "",
                      phone: fetchedComp.phone || block.content.phone || "",
                      email: fetchedComp.email || block.content.email || "",
                      address:
                        fetchedComp.address || block.content.address || "",
                    },
                  };
                }
                return block;
              });
              setBlocks(merged);
            }
          } else {
            // If pageIdParam provided but not found, just populate defaults
            setBlocks(
              getStrictReferenceBlocks("#f43f5e", "#ea580c", fetchedComp),
            );
          }
        } else {
          // If no custom landing page exists, populate defaults with real data
          setBlocks(
            getStrictReferenceBlocks("#f43f5e", "#ea580c", fetchedComp),
          );
        }
      } catch (err) {
        console.error("Error loading saved page or company data:", err);
      } finally {
        setIsInitializing(false);
      }
    };
    fetchData();
  }, [companyId, pageIdParam]);

  const applyGlobalPalette = (accent: string, secondary: string) => {
    setGlobalAccent(accent);
    setGlobalSecondary(secondary);
    setBlocks((prev) =>
      prev.map((b) => ({
        ...b,
        styles: {
          ...b.styles,
          accentColor: accent,
          secondaryColor: secondary,
        },
      })),
    );
  };

  const resetToFlexFitDesign = async () => {
    // Re-fetch fresh data from subcollections then rebuild blocks
    try {
      const allFacilities = new Set<string>();
      const allBenefits = new Set<string>();
      const allServices = new Set<string>();
      const branchesSnap = await getDocs(
        query(collection(db, "branch"), where("company_id", "==", companyId)),
      );
      await Promise.all(
        branchesSnap.docs.map(async (branchDoc) => {
          try {
            const facSnap = await getDocs(
              query(
                collection(db, "branch", branchDoc.id, "facilities"),
                where("company_id", "==", companyId),
              ),
            );
            facSnap.forEach((d) => {
              const data = d.data();
              if (data.title) allFacilities.add(data.title);
              else if (data.name) allFacilities.add(data.name);
            });
          } catch (e) {}
          try {
            const benSnap = await getDocs(
              query(
                collection(db, "branch", branchDoc.id, "benefits"),
                where("company_id", "==", companyId),
              ),
            );
            benSnap.forEach((d) => {
              const data = d.data();
              if (data.title) allBenefits.add(data.title);
              else if (data.name) allBenefits.add(data.name);
            });
          } catch (e) {}
          try {
            const serSnap = await getDocs(
              query(
                collection(db, "branch", branchDoc.id, "services"),
                where("company_id", "==", companyId),
              ),
            );
            serSnap.forEach((d) => {
              const data = d.data();
              if (data.name) allServices.add(data.name);
              else if (data.title) allServices.add(data.title);
            });
          } catch (e) {}
        }),
      );
      const freshData = {
        ...companyData,
        facilities: Array.from(allFacilities),
        benefits: Array.from(allBenefits),
        services: Array.from(allServices),
      };
      toast.success(
        `Reset: ${freshData.facilities.length} facilities, ${freshData.benefits.length} benefits`,
      );
      setCompanyData(freshData as any);
      setBlocks(
        getStrictReferenceBlocks(
          globalAccent,
          globalSecondary,
          freshData as any,
        ),
      );
    } catch (e) {
      setBlocks(
        getStrictReferenceBlocks(globalAccent, globalSecondary, companyData),
      );
    }
    setSelectedBlockId(null);
  };

  const savePageToFirestore = async () => {
    if (isSaving) return;
    if (!companyId) return;

    if (!firestorePageDocId) {
      const planLimit =
        plan.toLowerCase() === "max"
          ? 10
          : plan.toLowerCase() === "pro"
            ? 5
            : 1;
      try {
        const pagesQ = query(
          collection(db, "customlandingpages"),
          where("company_id", "==", companyId),
        );
        const pagesSnap = await getDocs(pagesQ);
        if (pagesSnap.size >= planLimit) {
          toast.error(
            `You've reached your limit of ${planLimit} landing pages for the ${plan} plan.`,
          );
          return;
        }
      } catch (e) {
        console.error("Error checking limits", e);
      }
    }

    setIsSaving(true);
    try {
      const newPageId = pageId || nanoid();
      const pageData = {
        page_id: newPageId,
        company_id: companyId,
        form_id: selectedFormIdUrl || "",
        blocks: blocks,
        globalAccent: globalAccent,
        globalSecondary: globalSecondary,
        updated_at: serverTimestamp(),
      };

      const baseUrl =
        process.env.REACT_APP_CURRENT_DOMAIN_NAME || window.location.origin;
      const url = `${baseUrl}/LandingPage/pageHtml?page_id=${newPageId}`;

      // Optimistic UI updates so Share and Update buttons appear immediately
      setPageId(newPageId);
      setSavedPageUrl(url);

      if (firestorePageDocId) {
        await updateDoc(
          doc(db, "customlandingpages", firestorePageDocId),
          pageData,
        );
      } else {
        // Generate a new reference synchronously so UI updates before network call
        const newDocRef = doc(collection(db, "customlandingpages"));
        setFirestorePageDocId(newDocRef.id);

        await setDoc(newDocRef, {
          ...pageData,
          created_at: serverTimestamp(),
        });
      }

      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 4000);
    } catch (err) {
      console.error("Error saving landing page:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const addBlockDirectly = (type: BlockType) => {
    const newBlock = createDefaultBlock(type, globalAccent, globalSecondary);
    setBlocks((prev) => [...prev, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  const moveBlock = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= blocks.length) return;
    const newBlocks = [...blocks];
    const [moved] = newBlocks.splice(index, 1);
    newBlocks.splice(targetIndex, 0, moved);
    setBlocks(newBlocks);
  };

  const duplicateBlock = (block: PageBlock, index: number) => {
    const duplicated: PageBlock = { ...block, id: nanoid() };
    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, duplicated);
    setBlocks(newBlocks);
    setSelectedBlockId(duplicated.id);
  };

  const allAvailableBlockTypes: BlockType[] = [
    "header",
    "hero",
    "services",
    "packages",
    "why_join",
    "testimonials",
    "cta_banner",
    "faq",
    "footer",
  ];

  const removedBlockTypes = allAvailableBlockTypes.filter(
    (t) => !blocks.some((b) => b.type === t),
  );

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  const getViewportWidth = () => {
    switch (viewport) {
      case "mobile":
        return "380px";
      case "tablet":
        return "768px";
      default:
        return "100%";
    }
  };

  if (isInitializing) {
    return (
      <div style={{ height: "100vh", position: "relative" }}>
        <BenchLoader isLoading={true} />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <ShareLandingPageModal
        opened={showUrlModal}
        onClose={() => setShowUrlModal(false)}
        savedPageUrl={savedPageUrl}
        availableForms={availableForms}
        selectedFormIdUrl={selectedFormIdUrl}
        onSelectFormIdUrl={setSelectedFormIdUrl}
      />

      <Container size="lg">
        {/* Header & Navigation */}
        <Box
          sx={(theme) => ({
            position: "sticky",
            top: 110,
            zIndex: 100,
            backgroundColor: "#FAFAFA",
            paddingTop: theme.spacing.sm,
            marginTop: `-${theme.spacing.sm}`,
            paddingBottom: theme.spacing.sm,
          })}
        >
          <SimpleGrid cols={2}>
            <BenchSettingsBreadcrumbs
              links={[
                { label: "Landing Pages", path: "/landingpages" },
                { label: "Landing Page Builder" },
              ]}
            />
            <Group position="right">
              {savedPageUrl && (
                <Button
                  leftIcon={<IconCopy size={16} />}
                  onClick={() => setShowUrlModal(true)}
                >
                  Share
                </Button>
              )}
              <Button
                size="sm"
                leftIcon={
                  showSaveSuccess ? (
                    <IconCheck size={16} />
                  ) : (
                    <IconDeviceFloppy size={16} />
                  )
                }
                loading={isSaving}
                onClick={savePageToFirestore}
              >
                {showSaveSuccess
                  ? "Saved!"
                  : firestorePageDocId
                    ? "Update Page"
                    : "Save Page"}
              </Button>
            </Group>
          </SimpleGrid>
          <Divider my="sm" />
        </Box>

        {/* Global Theme Palette Toolbar */}
        <Card
          withBorder
          radius="md"
          p="sm"
          mb="md"
          sx={{
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          <Group position="apart">
            <Group spacing="sm">
              <Box
                p={7}
                sx={{
                  borderRadius: "8px",
                  background: `linear-gradient(135deg, ${globalAccent}20, ${globalSecondary}20)`,
                  color: globalAccent,
                }}
              >
                <IconSparkles size={20} />
              </Box>
              <div>
                <Text
                  size="xs"
                  weight={800}
                  color="#0f172a"
                  transform="uppercase"
                >
                  Global Brand Themes
                </Text>
                <Text size="11px" color="dimmed">
                  Sync button gradients, badges, and icon highlights across all
                  sections
                </Text>
              </div>
            </Group>

            <Group spacing="xs">
              {GLOBAL_COLOR_PRESETS.map((preset) => (
                <Tooltip key={preset.name} label={preset.name}>
                  <ActionIcon
                    size="md"
                    radius="xl"
                    sx={{
                      background: `linear-gradient(135deg, ${preset.color} 0%, ${preset.secondary} 100%)`,
                      border:
                        globalAccent === preset.color
                          ? "2px solid #0f172a"
                          : "none",
                      transform:
                        globalAccent === preset.color ? "scale(1.15)" : "none",
                      transition: "transform 0.15s ease",
                    }}
                    onClick={() =>
                      applyGlobalPalette(preset.color, preset.secondary)
                    }
                  />
                </Tooltip>
              ))}
              <Divider orientation="vertical" />
              <ColorInput
                size="xs"
                label="Accent"
                value={globalAccent}
                onChange={(c) => applyGlobalPalette(c, globalSecondary)}
                format="hex"
                sx={{ width: 95 }}
              />
              <Button
                size="xs"
                variant="light"
                color="gray"
                leftIcon={<IconRotate size={13} />}
                onClick={resetToFlexFitDesign}
              >
                Reset
              </Button>
            </Group>
          </Group>
        </Card>

        <Grid gutter="lg">
          {/* Viewport Frame */}
          <Grid.Col span={12} lg={8}>
            <Stack spacing="md">
              <Group
                position="apart"
                sx={{
                  backgroundColor: "#ffffff",
                  padding: "10px 16px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                }}
              >
                <Group spacing={8}>
                  <IconAdjustmentsHorizontal size={18} color={globalAccent} />
                  <Text
                    size="xs"
                    weight={800}
                    color="#1e293b"
                    transform="uppercase"
                  >
                    Screen Viewport
                  </Text>
                </Group>

                <SegmentedControl
                  size="xs"
                  radius="md"
                  value={viewport}
                  onChange={(val: any) => setViewport(val)}
                  data={[
                    {
                      label: (
                        <Group spacing={4} noWrap position="center">
                          <IconDeviceDesktop size={14} /> Desktop
                        </Group>
                      ),
                      value: "desktop",
                    },
                    {
                      label: (
                        <Group spacing={4} noWrap position="center">
                          <IconDeviceTablet size={14} /> Tablet
                        </Group>
                      ),
                      value: "tablet",
                    },
                    {
                      label: (
                        <Group spacing={4} noWrap position="center">
                          <IconDeviceMobile size={14} /> Mobile
                        </Group>
                      ),
                      value: "mobile",
                    },
                  ]}
                />
              </Group>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  backgroundColor: "#cbd5e1",
                  padding: "24px 16px",
                  borderRadius: "16px",
                  minHeight: "800px",
                  overflowX: "auto",
                  border: "1px solid #94a3b8",
                }}
              >
                <Box
                  sx={{
                    width: getViewportWidth(),
                    maxWidth: getViewportWidth(),
                    backgroundColor: "#070b14",
                    borderRadius: "16px",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.45)",
                    overflow: "hidden",
                    transition: "width 0.3s ease",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Stack spacing={0}>
                    {blocks.map((block, index) => (
                      <BlockRenderer
                        key={block.id}
                        block={block}
                        isSelected={block.id === selectedBlockId}
                        onSelect={() => setSelectedBlockId(block.id)}
                        onRemove={() => {
                          setBlocks((prev) =>
                            prev.filter((b) => b.id !== block.id),
                          );
                          if (selectedBlockId === block.id)
                            setSelectedBlockId(null);
                        }}
                        onMoveUp={() => moveBlock(index, "up")}
                        onMoveDown={() => moveBlock(index, "down")}
                        onDuplicate={() => duplicateBlock(block, index)}
                        isFirst={index === 0}
                        isLast={index === blocks.length - 1}
                        viewport={viewport}
                        companyData={
                          companyData as FetchedCompanyData | undefined
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Grid.Col>

          {/* Inspector Panel */}
          <Grid.Col span={12} lg={4}>
            <Stack spacing="md">
              {selectedBlock ? (
                <SettingsPanel
                  block={selectedBlock}
                  updateBlock={(updated) => {
                    setBlocks((prev) =>
                      prev.map((b) => (b.id === updated.id ? updated : b)),
                    );
                  }}
                  companyData={companyData}
                />
              ) : (
                <Card
                  withBorder
                  radius="md"
                  p="xl"
                  sx={{
                    textAlign: "center",
                    backgroundColor: "#ffffff",
                    borderStyle: "dashed",
                  }}
                >
                  <IconEye
                    size={32}
                    color="#94a3b8"
                    style={{ margin: "0 auto 8px" }}
                  />
                  <Text weight={700} size="sm" color="#334155">
                    No Section Selected
                  </Text>
                  <Text color="dimmed" size="xs">
                    Click any section on the canvas to customize its text,
                    colors, spacing, and layout controls.
                  </Text>
                </Card>
              )}

              {/* Available Sections Drawer */}
              <Card
                withBorder
                shadow="xs"
                radius="md"
                p="md"
                sx={{ backgroundColor: "#ffffff" }}
              >
                <Title
                  order={5}
                  mb="xs"
                  sx={{ color: "#1e293b", fontSize: "14px" }}
                >
                  Hidden / Available Sections
                </Title>
                <Text size="xs" color="dimmed" mb="sm">
                  Re-add any previously deleted section back into your canvas:
                </Text>

                {removedBlockTypes.length === 0 ? (
                  <Paper
                    p="sm"
                    withBorder
                    sx={{
                      borderStyle: "dashed",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <Text size="xs" color="dimmed" align="center">
                      All sections are currently active on your canvas.
                    </Text>
                  </Paper>
                ) : (
                  <Stack spacing="xs">
                    {removedBlockTypes.map((type) => (
                      <Paper
                        key={type}
                        p="xs"
                        withBorder
                        radius="sm"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Group spacing="xs">
                          <IconLayersSubtract size={18} color={globalAccent} />
                          <div>
                            <Text size="xs" weight={700} transform="capitalize">
                              {type.replace("_", " ")}
                            </Text>
                            <Text size="10px" color="dimmed">
                              Layout Component
                            </Text>
                          </div>
                        </Group>
                        <Button
                          size="xs"
                          variant="light"
                          color="pink"
                          leftIcon={<IconPlus size={13} />}
                          onClick={() => addBlockDirectly(type)}
                        >
                          Add
                        </Button>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>
      {/* </Box> */}
    </DndProvider>
  );
};

export default LandingPageBuilder;
